import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Heading,
  Text,
  Loader,
  Flex,
  IconButton,
  AttentionBox,
  Modal,
  ModalContent,
  ModalFooterButtons,
  ModalHeader,
} from '@vibe/core';
import { Add, Delete, Edit, NavigationChevronRight, NavigationChevronLeft, CloseSmall } from '@vibe/icons';
import {
  listBoardConfigs,
  deleteBoardConfig,
  getBoardConfig,
  getBoardConfigsBulk,
  type BoardConfig,
} from '../../api/board-config.api';
import BoardPicker, { type PickedBoard } from '../shared/BoardPicker';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Props ────────────────────────────────────────────────────────────────────

interface BoardsConfigurationPanelProps {
  onEditBoard: (boardId: number) => void;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface BoardConfigWithName extends BoardConfig {
  boardName: string;
}

const PAGE_SIZE = 10;

// ─── Component ────────────────────────────────────────────────────────────────

const BoardsConfigurationPanel: React.FC<BoardsConfigurationPanelProps> = ({
  onEditBoard,
}) => {
  const [configs, setConfigs] = useState<BoardConfigWithName[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BoardConfigWithName | null>(null);

  // Search: user picks a board from monday.com, we look up its config by ID
  const [searchResult, setSearchResult] = useState<BoardConfigWithName | null>(null);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchBoard, setSearchBoard] = useState<PickedBoard | null>(null);

  // Add board dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<PickedBoard | null>(null);
  const [isAddingBoard, setIsAddingBoard] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const isSearchActive = searchResult !== null || searchNotFound;

  // Set of already-configured board IDs (from current page) to exclude from the Add Board picker.
  // Not exhaustive (only current page), but covers the common case.
  const configuredBoardIds = useMemo(
    () => new Set(configs.map((c) => c.boardId)),
    [configs],
  );

  // Fetch board names from monday.com for a list of board IDs
  const fetchBoardNames = useCallback(
    async (boardIds: number[]): Promise<Map<number, string>> => {
      const nameMap = new Map<number, string>();
      if (boardIds.length === 0) return nameMap;

      try {
        const query = `query ($ids: [ID!]!) { boards(ids: $ids) { id name } }`;
        const response = await monday.api(query, {
          variables: { ids: boardIds.map(String) },
        });
        const boards = (response.data as { boards?: { id: string; name: string }[] })?.boards;
        if (boards) {
          for (const b of boards) {
            nameMap.set(Number(b.id), b.name);
          }
        }
      } catch {
        // If we can't fetch names, we'll show IDs as fallback
      }
      return nameMap;
    },
    [],
  );

  const fetchConfigs = useCallback(
    async (p: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await listBoardConfigs(p, PAGE_SIZE);
        const boardIds = result.items.map((c) => c.boardId);
        const nameMap = await fetchBoardNames(boardIds);
        const withNames: BoardConfigWithName[] = result.items.map((c) => ({
          ...c,
          boardName: nameMap.get(c.boardId) ?? `Board ${c.boardId}`,
        }));
        setConfigs(withNames);
        setTotal(result.total);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load board configurations';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchBoardNames],
  );

  useEffect(() => {
    fetchConfigs(page);
  }, [page, fetchConfigs]);

  // Search: when user picks a board from monday.com, look up its config
  const handleSearchBoardChange = useCallback(
    async (board: PickedBoard | null) => {
      setSearchBoard(board);
      setSearchResult(null);
      setSearchNotFound(false);

      if (!board) return;

      const boardId = parseInt(board.id, 10);
      if (isNaN(boardId) || boardId <= 0) return;

      setIsSearching(true);
      try {
        // Use bulk endpoint which only returns existing configs (does NOT create new ones)
        const configMap = await getBoardConfigsBulk([boardId]);
        const config = configMap.get(boardId);
        if (config) {
          setSearchResult({
            ...config,
            boardName: board.name,
          });
          setSearchNotFound(false);
        } else {
          setSearchResult(null);
          setSearchNotFound(true);
        }
      } catch {
        setSearchResult(null);
        setSearchNotFound(true);
      } finally {
        setIsSearching(false);
      }
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchBoard(null);
    setSearchResult(null);
    setSearchNotFound(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteBoardConfig(deleteTarget.boardId);
      // If we deleted the search result, clear search
      if (searchResult && searchResult.boardId === deleteTarget.boardId) {
        handleClearSearch();
      }
      // Re-fetch the current page
      const newTotal = total - 1;
      const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
      const newPage = page > newTotalPages ? newTotalPages : page;
      setPage(newPage);
      await fetchConfigs(newPage);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete board configuration';
      setError(message);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, total, page, fetchConfigs, searchResult, handleClearSearch]);

  const handleAddBoard = useCallback(async () => {
    if (!selectedBoard) {
      setAddError('Please select a board');
      return;
    }

    const boardId = parseInt(selectedBoard.id, 10);
    if (isNaN(boardId) || boardId <= 0) {
      setAddError('Invalid board selection');
      return;
    }

    setIsAddingBoard(true);
    setAddError(null);
    try {
      // This creates a default config if it doesn't exist
      await getBoardConfig(boardId);
      setIsAddDialogOpen(false);
      setSelectedBoard(null);
      // Refresh list and go to first page to see the new entry
      setPage(1);
      await fetchConfigs(1);
    } catch (err) {
      setAddError(
        err instanceof Error ? err.message : 'Failed to add board configuration',
      );
    } finally {
      setIsAddingBoard(false);
    }
  }, [selectedBoard, fetchConfigs]);

  const handleCloseAddDialog = useCallback(() => {
    setIsAddDialogOpen(false);
    setSelectedBoard(null);
    setAddError(null);
  }, []);

  // Field mapping summary for a config
  const getMappingSummary = (config: BoardConfig): string => {
    const { fieldMapping } = config;
    const mapped: string[] = [];
    if (fieldMapping.timelineColumnId) mapped.push('Timeline');
    if (fieldMapping.assigneesColumnId) mapped.push('Assignees');
    if (fieldMapping.scheduledHoursColumnId) mapped.push('Hours');
    if (fieldMapping.statusColumnId) mapped.push('Status');
    if (fieldMapping.colorColumnId) mapped.push('Color');
    return mapped.length > 0 ? mapped.join(', ') : 'No mappings configured';
  };

  // Render a board config card
  const renderCard = (config: BoardConfigWithName) => (
    <div key={config.boardId} className="rp-boards-config__card">
      <div className="rp-boards-config__card-main">
        <div className="rp-boards-config__card-info">
          <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
            {config.boardName}
          </Text>
          <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color, #676879)', fontSize: 12 }}>
            ID: {config.boardId}
          </Text>
        </div>
        <Flex gap={Flex.gaps.XS}>
          <IconButton
            icon={Edit}
            size={IconButton.sizes.SMALL}
            onClick={() => onEditBoard(config.boardId)}
            ariaLabel={`Edit ${config.boardName}`}
          />
          <IconButton
            icon={Delete}
            size={IconButton.sizes.SMALL}
            onClick={() => setDeleteTarget(config)}
            ariaLabel={`Delete ${config.boardName}`}
          />
        </Flex>
      </div>
      <div className="rp-boards-config__card-meta">
        <div className="rp-boards-config__card-tag">
          {getMappingSummary(config)}
        </div>
        <div className="rp-boards-config__card-tag">
          {config.itemLevel === 'main'
            ? 'Main items'
            : config.itemLevel === 'sub'
              ? 'Sub-items'
              : 'Both'}
        </div>
        <div className="rp-boards-config__card-tag">
          Updated {new Date(config.updatedAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  if (isLoading && configs.length === 0) {
    return (
      <div className="rp-boards-config">
        <div className="rp-boards-config__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-boards-config">
      {/* Header */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.START}
        className="rp-boards-config__header"
      >
        <div style={{ minWidth: 0 }}>
          <Heading type={Heading.types.H3}>Boards Configuration</Heading>
          <Text type={Text.types.TEXT2} style={{ color: '#676879', marginTop: 2, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
            Pre-configure field mappings and settings for boards. Configured boards
            won&apos;t require setup when opened in the scheduler or board view.
          </Text>
        </div>
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={() => setIsAddDialogOpen(true)}
          style={{ flexShrink: 0 }}
        >
          Add Board
        </Button>
      </Flex>

      {/* Search: pick a board from monday.com to find its config */}
      <div className="rp-boards-config__search">
        <Text type={Text.types.TEXT2} style={{ color: '#676879', marginBottom: 4 }}>
          Find a configured board
        </Text>
        <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER}>
          <div style={{ flex: 1, maxWidth: 360 }}>
            <BoardPicker
              value={searchBoard}
              onChange={handleSearchBoardChange}
              placeholder="Search for a board by name..."
            />
          </div>
          {isSearchActive && (
            <IconButton
              icon={CloseSmall}
              size={IconButton.sizes.SMALL}
              kind={IconButton.kinds.TERTIARY}
              ariaLabel="Clear search"
              onClick={handleClearSearch}
            />
          )}
        </Flex>
      </div>

      {/* Search result */}
      {isSearching && (
        <div className="rp-boards-config__loading" style={{ padding: '24px 0' }}>
          <Loader size={Loader.sizes.SMALL} />
        </div>
      )}

      {searchResult && (
        <div className="rp-boards-config__list" style={{ marginBottom: 24 }}>
          {renderCard(searchResult)}
        </div>
      )}

      {searchNotFound && searchBoard && (
        <div className="rp-boards-config__empty" style={{ marginBottom: 24 }}>
          <Text>&quot;{searchBoard.name}&quot; is not configured yet.</Text>
        </div>
      )}

      {error && (
        <div className="rp-boards-config__error">
          <AttentionBox
            type={AttentionBox.types.DANGER}
            text={error}
            onClose={() => setError(null)}
          />
        </div>
      )}

      {/* Empty state */}
      {configs.length === 0 && !error && !isLoading && (
        <div className="rp-boards-config__empty">
          <Text>No boards configured yet. Add a board to pre-configure its field mappings and settings.</Text>
        </div>
      )}

      {/* Board cards (paginated list) */}
      {!isSearchActive && configs.length > 0 && (
        <>
          <div className="rp-boards-config__list">
            {configs.map(renderCard)}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex
              justify={Flex.justify.CENTER}
              align={Flex.align.CENTER}
              gap={Flex.gaps.SMALL}
              className="rp-boards-config__pagination"
            >
              <IconButton
                icon={NavigationChevronLeft}
                size={IconButton.sizes.SMALL}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                ariaLabel="Previous page"
              />
              <Text type={Text.types.TEXT2}>
                Page {page} of {totalPages}
              </Text>
              <IconButton
                icon={NavigationChevronRight}
                size={IconButton.sizes.SMALL}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                ariaLabel="Next page"
              />
            </Flex>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Board Configuration"
      >
        <ModalHeader title="Delete Board Configuration" />
        <ModalContent>
          <Text style={{ whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
            Are you sure you want to delete the configuration for &quot;{deleteTarget?.boardName}&quot;?
            This board will need to be reconfigured when next used.
          </Text>
        </ModalContent>
        <ModalFooterButtons
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          onPrimaryButtonClick={handleDeleteConfirm}
          onSecondaryButtonClick={() => setDeleteTarget(null)}
        />
      </Modal>

      {/* Add Board Dialog */}
      <Modal
        show={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        title="Add Board Configuration"
      >
        <ModalHeader title="Add Board Configuration" />
        <ModalContent className="rp-boards-config__add-modal-content">
          <Text style={{ marginBottom: 12, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
            Search and select a monday.com board to create a configuration for it.
            If you can&apos;t find a board here, it is already in your configuration.
          </Text>
          <BoardPicker
            value={selectedBoard}
            onChange={(board) => {
              setSelectedBoard(board);
              setAddError(null);
            }}
            placeholder="Search for a board…"
            excludeIds={configuredBoardIds}
          />
          {addError ? (
            <Text style={{ color: 'var(--negative-color, #d83a52)', marginTop: 8, fontSize: 13 }}>
              {addError}
            </Text>
          ) : <></>}
        </ModalContent>
        <ModalFooterButtons
          primaryButtonText="Add & Configure"
          secondaryButtonText="Cancel"
          onPrimaryButtonClick={handleAddBoard}
          onSecondaryButtonClick={handleCloseAddDialog}
        />
      </Modal>

      {/* Loading overlay for page changes */}
      {isLoading && configs.length > 0 && (
        <div className="rp-boards-config__loading-overlay">
          <Loader size={Loader.sizes.SMALL} />
        </div>
      )}
    </div>
  );
};

export default BoardsConfigurationPanel;
